import { uniqueKey } from 'global/common/keys'
import Checker from 'global/components/forms/elements/Checker'
import DropdownInput from 'global/components/forms/elements/DropdownInput'
import HtmlEditor from 'global/components/forms/elements/HtmlEditor'
import TextAreaInput from 'global/components/forms/elements/TextAreaInput'
import { FormItemsAlt } from 'global/components/forms/FormItemsAlt'
import { FormPreview } from 'global/components/forms/FormPreview'
import { gEnums } from 'global/enums/globalEnums'
import _ from 'lodash'
import { useContext, useMemo, useState } from 'react'
import { Divider, Dropdown, Form, Icon, Input } from 'semantic-ui-react'
import Wrapper, { wrapperTypes } from './Wrapper'
import { useFormOptions } from 'global/cnr/contexts/useHelpers/optionsBuilder/useFormOptions'
import { SquaresContext } from './SquaresWrapper'

const _showLogs = true

const SquaresForm = ({
  handle_formUpdate,
  currentGroup,
  currentData,
  currentPath,
  currentPathFull,
  currentViProps,
  onSave,
}) => {

  const squaresContext = useContext(SquaresContext)
  const { squares_state } = squaresContext ?? {}
  const { pageView } = squares_state ?? {}

  const [localData, setLocalData] = useState(currentData);
  const { groupItems, key: formKey } = currentGroup

  const [htmlProps, setHtmlProps] = useState()

  const _groupItems = useMemo(() => {
    return _.chain(groupItems)
      .cloneDeep()
      .sortBy('_position')
      .filter(gi => {
        const { showIfProp, showIfProps, showIfPropsValues } = gi?.data || {}

        // single-prop condition (existing behavior)
        if (showIfProp && !localData?.[showIfProp]) {
          return false
        }

        // multi-prop condition
        if (
          showIfProps &&
          showIfPropsValues &&
          Array.isArray(showIfProps) &&
          showIfProps.length === Object.keys(showIfPropsValues).length
        ) {
          let allow = true

          showIfProps.forEach((propKey, index) => {
            try {
              const formValue = localData?.[propKey]
              const allowedValues = showIfPropsValues[index + 1]

              if (!formValue || !Array.isArray(allowedValues)) {
                allow = false
                return
              }

              if (!allowedValues.includes(formValue)) {
                allow = false
              }
            } catch (err) {
              console.error(err)
              allow = false
            }
          })

          return allow
        }

        return true
      })
      .value()
  }, [groupItems, localData])

  const _optionsList = useFormOptions({ localData, currentGroup, currentViProps, groupItems, currentData, pageView })

  const debouncedUpdate = useMemo(
    () =>
      _.debounce((data) => {
        handle_formUpdate(data, currentPath, currentPathFull);
      }, 300),
    [currentPath, handle_formUpdate]
  );

  const handleChange = (propName, value, c, list) => {
    const _propName = propName || value?.propname

    let _value;

    if (list) {
      _value = list
    } else {
      if (_.isObject(value)) {
        if (value.value) {
          _value = value.value
        } else {
          _value = value
        }
      } else {
        _value = value
      }
    }

    if (_showLogs) {
      console.group('Handle Change')
      console.log('formKey', formKey)
      console.log('localData', localData)
      console.log('_propName', _propName)
      console.log('value before', _value)
      console.log(`Changes the value of '${_propName}' in '${formKey}'`)
    }

    setLocalData(prev => {
      const next = _.cloneDeep(prev);
      _.set(next, _propName, _value);
      if (_showLogs) {
        console.log('value after', next)
        console.groupEnd()
      }
      debouncedUpdate(next);
      return next;
    });
  };

  const handleEditor = (editorProps, prop) => setHtmlProps(!htmlProps ? editorProps : null)

  const handleUpdate_html = (a, b) => {
    const { propname, value } = b ?? {}
    handleChange(propname, value)
    setHtmlProps()
  }

  const divHeader = (bb, key) => <Divider className='inl' key={uniqueKey('fi', 'bb', key)} horizontal >
    <Icon name={'object ungroup outline'} color={'blue'} style={{ marginRight: '1em' }} />
    {bb}
  </Divider>

  const renderFormItem = (key, item) => {
    const { prop, data, ui } = item;
    const _key = prop || key
    const { formItemType, placeholder, listType, breakBefore, previewType } = data ?? {};
    const { caption: label } = ui ?? {};
    const _value = localData[_key]

    let _formItemType = (formItemType) || 'text'
    switch (currentGroup.key) {
      case 'themeColors':
        _formItemType = gEnums.formItemTypes.colorPicker
    }

    const fip = { propname: _key, value: _value, onChange: handleChange, label }
    const itemProp = { fip }

    switch (_formItemType) {
      case gEnums.formItemTypes.checkbox:
        return (
          <>
            {breakBefore && divHeader(breakBefore, _key)}
            <Checker key={_key} item={fip} label={label} prop={prop} handleClick={handleChange} checked={_value} wrapperType={'inline'} />
          </>
        );

      case gEnums.formItemTypes.text:
        return (
          <>
            {breakBefore && divHeader(breakBefore, _key)}
            <Form.Field key={_key}>
              <label>{label}</label>
              <Input
                placeholder={placeholder || ''}
                value={_value}
                onChange={(e) => handleChange(prop, e.target.value)}
              />
            </Form.Field>
          </>
        );

      case gEnums.formItemTypes.dropdown:
      case gEnums.formItemTypes.select:
        const hasOptions = _optionsList?.[listType] ? true : false
        return (
          <>
            {breakBefore && divHeader(breakBefore, _key)}
            <Form.Field key={_key}>
              <label style={{ color: hasOptions ? null : 'var(--sem-red-light)!important' }}>{label}</label>
              <Dropdown
                placeholder={placeholder || ''}
                fluid
                search
                selection
                // multiple 
                options={_optionsList?.[listType] || []}
                value={_value}
                className='swiper-no-swiping'
                onChange={(_, { value }) => handleChange(prop, value)}

              />
            </Form.Field>
          </>
        );

      case gEnums.formItemTypes.textArea:
      case gEnums.formItemTypes.textAreaSimple:
        return <TextAreaInput fip={fip} itemProp={{}} prop={prop} handleEditor={handleEditor} formItemType={_formItemType} />

      case gEnums.formItemTypes.dropdown:
      case gEnums.formItemTypes.dropdownAdd:
      case gEnums.formItemTypes.listSelect:
        return <DropdownInput fip={fip} optionList={_optionsList} selectMulti={true} itemProp={{}} />

      case gEnums.formItemTypes.appUserDataModificationList:
      case gEnums.formItemTypes.fileSelect:
      case gEnums.formItemTypes.gridRowSelect:
      case gEnums.formItemTypes.gridRowSelectFull:
      case gEnums.formItemTypes.menuItemSelect:
      case gEnums.formItemTypes.menuItemSelectFull:
      case gEnums.formItemTypes.menuItemSelectFullCount:
      case gEnums.formItemTypes.backgroundColorSelectSingle:
      case gEnums.formItemTypes.colorSelect:
      case gEnums.formItemTypes.colorSelectSingle:
      case gEnums.formItemTypes.colorPicker:
      case gEnums.formItemTypes.slider:
      case gEnums.formItemTypes.listAdd:
      case gEnums.formItemTypes.listView:
      case gEnums.formItemTypes.itemMap:
      case gEnums.formItemTypes.itemArrayMulti:
        return <FormItemsAlt
          formItemType={_formItemType}
          fip={fip}
          localData={localData}
          optionsList={_optionsList}
          itemProp={itemProp}
          _key={_key}
          prop={prop}
          item={item}
          handleChange={handleChange}
          formKey={formKey}
        ></FormItemsAlt>

      // Add other formItemTypes here
      default:
        if (previewType) {
          return <div>
            {divHeader('Preview', _key)}
            <FormPreview
              previewType={previewType}
              formItemType={_formItemType}
              itemProp={itemProp}
              localData={localData}
            />
          </div>
        }
        return (
          <Form.Field key={_key}>
            <label>{`${_formItemType} ?`}</label>
            <Input
              placeholder={_formItemType || ''}
              value={_formItemType || _key}
            // onChange={(e) => handleChange(key, e.target.value)}
            />
          </Form.Field>
        );
    }
  };

  const handleSave = () => {
    if (onSave) onSave(localData);
  };

  const form = () => <Form style={{ padding: '0em' }} className={'frm-sf'}>
    {Object.entries(_groupItems).map(([key, item]) => renderFormItem(key, item))}
  </Form>

  const htmlForm = () => <HtmlEditor
    currentFip={{ propname: htmlProps.propname, value: htmlProps.value, label: 'test' }}
    {...htmlProps}
    handleUpdate={handleUpdate_html}
    handleEditor={handleEditor}
  />

  return (
    <Wrapper
      content={htmlProps ? htmlForm() : form()}
      wrapperType={wrapperTypes.paddedFooter}
    ></Wrapper >
  );
};

export default SquaresForm;

const getCurrentViPropList = (localData, aps_viewItems, currentGroup) => {

  let _currentViPropList = null
  let _currentCollectionPropList = null

  const { captionPage, dataCollectionName } = localData ?? {}

  let _vit; //= vit || '' 

  if (dataCollectionName) { _vit = dataCollectionName }

  if (aps_viewItems && _vit && aps_viewItems[_vit]) {
    let currentVi = aps_viewItems[_vit]
    const { altCollectionName } = currentVi ?? {}
    if (altCollectionName && aps_viewItems[altCollectionName]) {
      currentVi = aps_viewItems[altCollectionName]
    }
    const { props: props_vi, propsAdditional } = currentVi ?? {}
    const { ignoreItemStatus, showPdf } = propsAdditional ?? {}
    if (useItemStatus && !ignoreItemStatus && isDataItem && !ignoreItemStatus_p) { props_vi['itemStatusType'] = {} }
    if (useItemTimezone && !ignoreItemStatus && isDataItem && !ignoreItemStatus_p) { props_vi['itemStatusType'] = {} }
    if (showPdf) { props_vi['itemPdf'] = {} }
    _currentViPropList = props_vi
  }

  if (captionPage && aps_viewItems && aps_viewItems[captionPage]) { _currentCollectionPropList = aps_viewItems[captionPage].props }
  // if (menu && menu.captionPage && aps_viewItems && aps_viewItems[menu.captionPage]) { _currentCollectionPropList = aps_viewItems[menu.captionPage].props }

  return { _currentViPropList, _currentCollectionPropList }

}

const groupByDividedText = (groupItems = [], divider, divider2) => {
  return _.transform(
    _.sortBy(groupItems, '_position'),
    (acc, item) => {
      if (item?.[divider]) {
        const key = divider2 ? item?.[divider]?.[item[divider2]] : item?.[divider]
        acc.push({
          key: key,
          items: []
        });
      }

      if (!acc.length) return;

      acc[acc.length - 1].items.push(item);
    },
    []
  );
};
